import { use } from 'react'
import { Toaster as Sonner, type ToasterProps } from 'sonner'

import { Icon } from '@/components/ui/icon'
import { DARK_THEMES, ThemeContext } from '@/hooks/useTheme'

/*
 * Toast (design board 08): the result of something the user just did.
 * Bottom right, 24 from the edges, at most three, newest at the bottom. A
 * surface card with a border, shadow lg and radius 12, a tone icon and one
 * sentence; at most one action, written as a verb.
 *
 * Sonner only needs to know light or dark, for its own fallbacks — every
 * colour it shows comes from the tokens below. Read from the context without
 * requiring it, so a test that mounts a page with its own toaster and no
 * theme provider still renders.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const theme = use(ThemeContext)?.theme ?? 'default'

  return (
    <Sonner
      theme={DARK_THEMES.has(theme) ? 'dark' : 'light'}
      className="toaster group"
      position="bottom-right"
      offset={24}
      visibleToasts={3}
      duration={5000}
      closeButton
      icons={{
        success: <Icon name="check_circle" className="text-success" />,
        info: <Icon name="info" className="text-info" />,
        warning: <Icon name="warning" className="text-warning" />,
        error: <Icon name="error" className="text-error" />,
        loading: <Icon name="progress_activity" className="text-muted-foreground animate-spin" />,
        close: <Icon name="close" size={16} />,
      }}
      toastOptions={{
        classNames: {
          toast:
            'bg-surface! text-foreground! border-border! rounded-lg! shadow-lg! gap-3! py-3! pr-12! pl-4! font-sans! text-sm!',
          title: 'font-normal! text-sm!',
          description: 'text-foreground-secondary! text-sm!',
          icon: 'mt-0.5! size-5! self-start!',
          actionButton:
            'bg-transparent! text-primary-text! font-semibold! text-cell! px-2! hover:underline!',
          cancelButton: 'bg-transparent! text-foreground-secondary! font-semibold! text-cell!',
          closeButton:
            'left-auto! right-2! top-2.5! translate-none! size-7! rounded-sm! border-0! bg-transparent! text-muted-foreground! hover:bg-overlay-hover! hover:text-foreground!',
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
